import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { auditLogsApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  Search,
  Filter,
  ClipboardList,
  User,
  Target,
  Activity,
  Shield,
  FileText
} from 'lucide-react';

const ENTITY_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'user', label: 'Users' },
  { value: 'exercise', label: 'Exercises' },
  { value: 'inject', label: 'Injects' },
  { value: 'response', label: 'Responses' },
  { value: 'organisation', label: 'Organisations' },
];

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');

  useEffect(() => {
    loadLogs();
  }, [entityFilter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await auditLogsApi.getAll(
        entityFilter !== 'all' ? entityFilter : null,
        null,
        null,
        200
      );
      setLogs(data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'user': return <User className="w-4 h-4" />;
      case 'exercise': return <Target className="w-4 h-4" />;
      case 'inject': return <Activity className="w-4 h-4" />;
      case 'organisation': return <Shield className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getActionColor = (action) => {
    if (action.includes('create') || action.includes('register')) return 'text-green-500 bg-green-500/10';
    if (action.includes('delete')) return 'text-red-500 bg-red-500/10';
    if (action.includes('update') || action.includes('edit')) return 'text-blue-500 bg-blue-500/10';
    if (action.includes('login') || action.includes('logout')) return 'text-purple-500 bg-purple-500/10';
    return 'text-muted-foreground bg-muted0/10';
  };

  const filteredLogs = logs.filter(log => {
    const searchLower = searchQuery.toLowerCase();
    return (
      log.action?.toLowerCase().includes(searchLower) ||
      log.entity_type?.toLowerCase().includes(searchLower) ||
      log.entity_id?.toLowerCase().includes(searchLower) ||
      log.user_id?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <DashboardLayout title="Audit Logs">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-logs"
              />
            </div>
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className="w-[160px]" data-testid="entity-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button variant="outline" onClick={loadLogs}>
            Refresh
          </Button>
        </div>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="py-12 text-center">
                <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No audit logs found</p>
                <p className="text-muted-foreground">
                  {searchQuery || entityFilter !== 'all' 
                    ? 'Try adjusting your search or filters'
                    : 'System activity will appear here'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>User ID</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log) => (
                    <TableRow key={log.log_id}>
                      <TableCell className="font-mono text-sm">
                        {log.created_at && new Date(log.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="secondary" 
                          className={`${getActionColor(log.action)} capitalize`}
                        >
                          {log.action?.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getEntityIcon(log.entity_type)}
                          <span className="capitalize">{log.entity_type}</span>
                          {log.entity_id && (
                            <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                              ({log.entity_id})
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs truncate max-w-[150px]">
                        {log.user_id}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <div className="text-xs text-muted-foreground truncate">
                            {JSON.stringify(log.details).slice(0, 50)}...
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {log.ip_address || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        {!loading && filteredLogs.length > 0 && (
          <div className="text-sm text-muted-foreground text-center">
            Showing {filteredLogs.length} log entries
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
